/**
 * The store's money on the web: which currency it counts in and which it
 * sells in, as the API says (`store {store_config}` → currency, currencies —
 * aldoo-na/store/currency.js, set by STORE_CURRENCY / SUPPORTED_CURRENCIES
 * in the project's config or in ParamIO). Nothing here names a currency.
 *
 *   const { currency, currencies } = useStoreCurrency()
 *   formatMoney(1250, currency, { cents: true })   // "$12.50", "12,50 €" …
 */
import { useEffect, useState } from "react"
import { prefetchStripeConfig } from "./stripe-client"

let known = null // { currency, currencies } once the config answered

/** The store config's money, fetched once and shared (store_config is cached). */
export async function loadStoreCurrency() {
  // the server asks again as the config expires (stripe-client); the browser keeps it for the page
  if (known && typeof window !== "undefined") return known
  const cfg = await prefetchStripeConfig()
  if (cfg?.currency) known = { currency: cfg.currency, currencies: cfg.currencies?.length ? cfg.currencies : [cfg.currency] }
  return known || { currency: null, currencies: [] }
}

/** `{ currency, currencies, ready }`: null / [] until the store config has answered. */
export function useStoreCurrency() {
  const [value, setValue] = useState(known || { currency: null, currencies: [] })
  useEffect(() => {
    if (known) return
    let alive = true
    loadStoreCurrency().then((v) => alive && setValue(v))
    return () => {
      alive = false
    }
  }, [])
  return { ...value, ready: !!value.currency }
}

/**
 * An amount in `currency`, as the reader's locale writes money. `cents`: the
 * amount is in the smallest unit (the dashboards' figures). `compact`: no
 * decimals on large amounts. Without a currency the number alone.
 */
export function formatMoney(amount, currency, { cents = false, compact = false, locale } = {}) {
  const n = (Number(amount) || 0) / (cents ? 100 : 1)
  if (!currency) return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n)
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency, currencyDisplay: "narrowSymbol", maximumFractionDigits: compact && Math.abs(n) >= 1000 ? 0 : 2 }).format(n)
  } catch {
    return `${n.toFixed(2)} ${currency}`
  }
}

/** The symbol a currency is written with in the reader's locale ("$", "€", "лв." …). */
export function currencySymbol(currency, locale) {
  if (!currency) return ""
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency, currencyDisplay: "narrowSymbol" }).formatToParts(0).find((p) => p.type === "currency")?.value || currency
  } catch {
    return currency
  }
}
